import { getFirebaseAuth } from './config';
import { logError, logInfo } from '../logging/logger';

interface PairingResponse {
  status: 'paired';
  deviceId: string;
}

interface UnpairResponse {
  status: 'unpaired';
}

/**
 * Returns the endpoint URL for headset pairing functions.
 */
function getPairingUrl(path: string): string {
  const isProduction = window.location.hostname !== 'localhost';
  if (isProduction) {
    return `/api/${path}`;
  }

  const base = import.meta.env.VITE_FIREBASE_FUNCTIONS_BASE_URL || '';
  return base ? `${base}/${path}` : `/api/${path}`;
}

/**
 * Claims a pairing code for the current user.
 */
export async function claimPairingCode(
  code: string,
  deviceName: string
): Promise<PairingResponse> {
  const token = await requireAuthToken();
  logInfo('Headset pairing claim requested.', { code });
  const response = await fetch(getPairingUrl('claimPairingCode'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ code, deviceName }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    logError('Headset pairing claim failed.', {
      status: response.status,
      error: error.error ?? 'Unknown error',
    });
    throw new Error(error.error || 'Failed to pair device.');
  }

  return response.json() as Promise<PairingResponse>;
}

/**
 * Unpairs a headset device for the current user.
 */
export async function unpairDevice(deviceId: string): Promise<UnpairResponse> {
  const token = await requireAuthToken();
  logInfo('Headset unpair requested.', { deviceId });
  const response = await fetch(getPairingUrl('unpairDevice'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ deviceId }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    logError('Headset unpair failed.', {
      status: response.status,
      error: error.error ?? 'Unknown error',
    });
    throw new Error(error.error || 'Failed to unpair device.');
  }

  return response.json() as Promise<UnpairResponse>;
}

/**
 * Ensures the user is authenticated and returns the ID token.
 */
async function requireAuthToken(): Promise<string> {
  const auth = getFirebaseAuth();
  if (!auth.currentUser) {
    throw new Error('Authentication required.');
  }

  return auth.currentUser.getIdToken();
}
